/** 内容详情：基本信息、预览、布置、删除（与 iOS ContentDetailView 一致） */
import { FileX } from "lucide-react";
import { useState } from "react";
import { type Content, confirm, deleteEntity, run, useCalendar, useDevSheet, useStore } from "../../core";
import { Button, EmptyHint, FormCard, Page, RowDivider, ValueRow } from "../../ui";
import { activityName } from "./activity";
import { BackHeader, TypeBlock } from "./Library";
import { PreviewSheet } from "./Preview";

export function ContentDetail({ contentId, onBack, onAssign }: { contentId: string; onBack: () => void; onAssign: (c: Content) => void }) {
  const c = useStore((s) => s.contents[contentId]);
  const folder = useStore((s) => (c?.folderId ? s.folders[c.folderId] : undefined));
  const tags = useStore((s) => s.tags);
  const assignedCount = useStore((s) => Object.values(s.assignments).filter((a) => a.contentId === contentId).length);
  const cal = useCalendar();
  const [previewing, setPreviewing] = useDevSheet("preview");
  const [deleting, setDeleting] = useState(false);

  if (!c) {
    return (
      <Page>
        <BackHeader title="内容详情" onBack={onBack} />
        <EmptyHint icon={FileX} text="内容已删除" />
      </Page>
    );
  }

  const tagText = (c.tagIds ?? [])
    .map((id) => tags[id]?.name)
    .filter(Boolean)
    .map((n) => `#${n}`)
    .join(" ");

  const remove = async () => {
    const ok = await confirm({ title: "删除这份内容？", message: "已经布置的学习也会无法打开。", confirmText: "删除", danger: true });
    if (!ok) return;
    setDeleting(true);
    const done = await run(async () => {
      await deleteEntity("contents", c.id, `contents/${c.id}`);
      return true;
    }, "已删除");
    setDeleting(false);
    if (done) onBack();
  };

  return (
    <Page gap={12}>
      <BackHeader title="内容详情" onBack={onBack} />
      <div className="learn-detail-head">
        <TypeBlock type={c.activityType} width={56} height={72} radius={8} size={13} />
        <div>
          <h2>{c.title}</h2>
          {c.createdAt != null && <p>添加于 {cal.relativeDay(cal.dateString(c.createdAt))}</p>}
        </div>
      </div>
      <FormCard>
        <ValueRow title="类型" value={activityName(c.activityType)} />
        <RowDivider />
        <ValueRow title="文件夹" value={folder?.name ?? "未分类"} />
        <RowDivider />
        <ValueRow title="标签" value={tagText || "无"} />
        {c.estimatedMin != null && (
          <>
            <RowDivider />
            <ValueRow title="预计用时" value={`${c.estimatedMin} 分钟`} />
          </>
        )}
        <RowDivider />
        <ValueRow title="布置过" value={`${assignedCount} 次`} />
      </FormCard>
      {c.description && (
        <FormCard>
          <p className="learn-desc">{c.description}</p>
        </FormCard>
      )}
      <div className="learn-detail-actions">
        <Button variant="soft" height={52} onClick={() => setPreviewing(true)}>
          预览
        </Button>
        <Button height={52} onClick={() => onAssign(c)}>
          布置给孩子
        </Button>
      </div>
      {c.activityType === "web_activity" && <p className="learn-note">网页版不能运行「{activityName(c.activityType)}」，只能查看信息；布置后在平板上照常使用。</p>}
      <Button variant="ghost" className="learn-danger-btn" block busy={deleting} onClick={() => void remove()}>
        删除内容
      </Button>
      <PreviewSheet content={c} open={previewing} onClose={() => setPreviewing(false)} />
    </Page>
  );
}
